import {
    GoalDataLevels,
    IAdgroups,
    ICampaigns,
    IGoal,
    IKeywords,
    IPortfolios,
    IProducts,
    IValueBoolean,
    IValueString,
    TGoal,
    TGoalDataValues,
    TGoalsConfig,
    TGoalsData,
} from "@/ts/common-types";
import {
    Button,
    Col,
    Form,
    Input,
    Modal,
    Radio,
    Row,
    Select,
    DatePicker,
    Tooltip,
} from "antd";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    setGoalEditConfig,
    setIsGoalFormSubmitted,
    updateAddGoalsModalDisplay,
} from "@/features/modalSlice";
import { DATA_VALUE_MAPPING } from "@/ts/constants";
import { find, isArray, keys, map, reduce, values } from "underscore";
import { MdDeleteOutline } from "react-icons/md";
import dayjs from "dayjs";
import { addGoals, getDataLevelConfig } from "@/ts/utils";

const { RangePicker } = DatePicker;

type IAddGoalsModalProps = {
    show: boolean;
};

const AddGoalsModal: React.FC<IAddGoalsModalProps> = ({ show }) => {
    const { Option } = Select;
    const dispatch = useDispatch();

    const goalModalTitle = useSelector(
        (state: { goalModalTitle: IValueString }) => state.goalModalTitle.value
    );

    const goalEditConfig = useSelector(
        (state: IGoal) => state.goalEditConfig.value
    );

    const isGoalEdit = !!keys(goalEditConfig).length;

    const [dataLevel, setDataLevel] = useState<string>("");
    const [isDataFilterReq, setIsDataFilterReq] = useState<boolean>(false);
    const [isDateRangePickerReq, setIsDateRangePickerReq] =
        useState<boolean>(false);

    const [dataValueFilterOptions, setDataValueFilterOptions] = useState<any>([
        { value: "equals", label: "Equals", type: "multiSelect" },
    ]);
    const [dataValueFilter, setDataValueFilter] = useState<string>("equals");
    const [isDataValueReq, setIsDataValueReq] = useState<boolean>(true);
    const [dataValues, setDataValues] = useState<string[]>([]);
    const [metricLevelOptions, setMetricLevelOptions] = useState<string[]>([]);
    const [metricLevelFilterOptions, setMetricLevelFilterOptions] =
        useState<any>([]);
    const [filterConditions, setFilterConditions] = useState<string[]>([]);

    const [goalPeriodOptions, setGoalPeroidOptions] = useState<any>([]);

    const [conditionCounter, setConditionCounter] = useState<number>(0);

    const isGoalFormSubmitted = useSelector(
        (state: { isGoalFormSubmitted: IValueBoolean }) =>
            state.isGoalFormSubmitted.value
    );
    const [form] = Form.useForm();

    const goalsConfig = useSelector(
        (state: { goalsConfig: { value: TGoalsConfig[] } }) =>
            state.goalsConfig.value
    );

    const portfoliosList = map(
        useSelector((state: IPortfolios) => state.portfoliosData.value),
        (portfolio) => portfolio.portfolio_name
    );

    const campaignsList = map(
        useSelector((state: ICampaigns) => state.campaignsData.value),
        (campaign) => campaign.campaign_name
    );

    const adgroupsList = map(
        useSelector((state: IAdgroups) => state.adgroupsData.value),
        (adgroup) => adgroup.ad_group_name
    );

    const keywordsList = map(
        useSelector((state: IKeywords) => state.keywordsData.value),
        (keyword) => keyword.keyword_name
    );

    const productsList = map(
        useSelector((state: IProducts) => state.productsData.value),
        (product) => product.sku_title
    );

    const goalDataValues = useSelector(
        (state: { goalDataValues: { value: TGoalDataValues } }) =>
            state.goalDataValues.value
    );

    const goalDataValuesList: Record<string, string[]> = useMemo(
        () => ({
            ...goalDataValues,
            portfoliosList,
            campaignsList,
            adgroupsList,
            keywordsList,
            productsList,
        }),
        [
            goalDataValues,
            portfoliosList,
            campaignsList,
            adgroupsList,
            keywordsList,
            productsList,
        ]
    );

    //Data Level
    const dataLevelOptions = map(goalsConfig, (config) => config.data_level);

    const handleDataLevelChange = (value: GoalDataLevels) => {
        form.resetFields([
            "dataValueInput",
            "dataValueFilter",
            "metric",
            "metricValueFilter",
            "metricValueInput"
        ]);
        setDataLevel(value);
        setDataValueFilter(
            isGoalEdit && value === goalEditConfig.data_level
                ? goalEditConfig.data_level_filter
                : "equals"
        );
        setIsDataValueReq(value !== "Account");
        const dataLevelConfig = getDataLevelConfig(goalsConfig, value);

        const isDataLevelFilterable = dataLevelConfig.search_option.length;

        setIsDataFilterReq(!!isDataLevelFilterable);
        setDataValueFilterOptions(
            isDataLevelFilterable
                ? dataLevelConfig.search_option
                : [{ value: "equals", label: "Equals", type: "multiSelect" }]
        );
        setDataValues(goalDataValuesList[DATA_VALUE_MAPPING[value]]);
        setMetricLevelOptions(keys(dataLevelConfig.metric_level));
        setMetricLevelFilterOptions(dataLevelConfig.metric_level_condition);
        setFilterConditions(keys(dataLevelConfig.filter_value));
        setGoalPeroidOptions(dataLevelConfig.time_selection);
    };

    const handleDataLevelFilterChange = (value: string) => {
        setDataValueFilter(value);
        form.resetFields(["dataValueInput"]);
    };

    const renderDataValueInput = () => {
        const selectedOption = dataValueFilterOptions.find(
            (option: any) => option.value === dataValueFilter
        );

        if (selectedOption?.type === "input") {
            return (
                <Form.Item
                    name="dataValueInput"
                    style={{ flex: 2, marginBottom: 0 }}
                    rules={[
                        {
                            required: isDataValueReq,
                            message: "Please enter a value",
                        },
                    ]}
                    validateTrigger={
                        isGoalFormSubmitted
                            ? ["onChange", "onBlur"]
                            : "onSubmit"
                    }
                >
                    <Input
                        style={{
                            border: "none",
                            borderRadius: 0,
                        }}
                        placeholder={`Enter value`}
                    />
                </Form.Item>
            );
        } else if (selectedOption?.type === "multiSelect") {
            return (
                <Form.Item
                    name="dataValueInput"
                    style={{ flex: 2, marginBottom: 0 }}
                    rules={[
                        {
                            required: isDataValueReq,
                            message: "Please select a value",
                        },
                    ]}
                    validateTrigger={
                        isGoalFormSubmitted
                            ? ["onChange", "onBlur"]
                            : "onSubmit"
                    }
                >
                    <Select
                        mode="multiple"
                        bordered={false}
                        allowClear
                        placeholder={`Select value`}
                        maxTagCount={1}
                        maxTagTextLength={25}
                        maxTagPlaceholder={(omittedValues) => (
                            <Tooltip
                                overlayStyle={{ pointerEvents: "none" }}
                                title={
                                    <div>
                                        {omittedValues.map(({ label }) => (
                                            <div key={label.toString()}>
                                                {label}
                                            </div>
                                        ))}
                                    </div>
                                }
                            >
                                <span>+{omittedValues.length}</span>
                            </Tooltip>
                        )}
                        style={{
                            border: "none",
                            borderRadius: 0,
                        }}
                    >
                        {dataValues?.map((dataValue) => (
                            <Option key={dataValue} value={dataValue}>
                                {dataValue}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>
            );
        }
        return null;
    };

    //Condition
    type TCondition = {
        key: number;
        type: string;
        value: string[];
        options: string[];
    };

    const [conditions, setConditions] = useState<TCondition[]>([
        { key: 0, type: null, value: [], options: [] },
    ]);

    const handleFilterConditionChange = (value: string, index: number) => {
        const dataLevelConfig = getDataLevelConfig(goalsConfig, dataLevel);
        setConditions(
            map(conditions, (condition) =>
                condition.key === index
                    ? {
                        ...condition,
                        type: value,
                        options: dataLevelConfig.filter_value[value],
                        value: [],
                    }
                    : condition
            )
        );
        form.resetFields([`conditionValue${index}`]);
    };

    const handleAddCondition = () => {
        setConditionCounter(conditionCounter + 1);
        setConditions([
            ...conditions,
            { key: conditionCounter + 1, type: null, value: [], options: [] },
        ]);
    };

    const handleRemoveCondition = (index: number) => {
        setConditions(
            conditions.filter((condition) => condition.key !== index)
        );
    };

    const renderExtraConditions = () =>
        map(conditions, (condition, i) => (
            <Row gutter={16} key={condition.key}>
                <Col span={12}>
                    <Form.Item
                        name={`condition${condition.key}`}
                        label={`Condition - ${i + 1}`}
                    >
                        <Select
                            allowClear
                            placeholder="Select condition"
                            value={condition.type}
                            onChange={(value) =>
                                handleFilterConditionChange(
                                    value,
                                    condition.key
                                )
                            }
                        >
                            {map(filterConditions, (condition) => (
                                <Option value={condition}>{condition}</Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item style={{ marginBottom: 0 }}>
                        <Input.Group style={{ display: "flex" }}>
                            <Form.Item
                                name={`conditionValue${condition.key}`}
                                label={`Condition Value - ${i + 1}`}
                                style={{ flex: 12 }}
                            >
                                <Select
                                    style={{}}
                                    mode="multiple"
                                    allowClear
                                    value={condition.value}
                                    maxTagCount={3}
                                    maxTagTextLength={30}
                                    maxTagPlaceholder={(omittedValues) => (
                                        <Tooltip
                                            overlayStyle={{
                                                pointerEvents: "none",
                                            }}
                                            title={
                                                <div>
                                                    {map(
                                                        omittedValues,
                                                        ({ label }) => (
                                                            <div
                                                                key={label.toString()}
                                                            >
                                                                {label}
                                                            </div>
                                                        )
                                                    )}
                                                </div>
                                            }
                                        >
                                            <span>+{omittedValues.length}</span>
                                        </Tooltip>
                                    )}
                                    placeholder="Select condition value"
                                    onChange={(value) =>
                                        setConditions(
                                            map(conditions, (c) =>
                                                c.key === condition.key
                                                    ? { ...c, value: value }
                                                    : c
                                            )
                                        )
                                    }
                                >
                                    {map(condition.options, (condition) => (
                                        <Option value={condition}>
                                            {condition}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                            <Form.Item
                                style={{
                                    flex: 1,
                                    marginLeft: 5,
                                    display: !condition.key ? "none" : "unset",
                                }}
                                id={`remove-condition-${condition.key}`}
                                label=" "
                            >
                                <Button
                                    icon={<MdDeleteOutline />}
                                    danger
                                    onClick={() =>
                                        handleRemoveCondition(condition.key)
                                    }
                                ></Button>
                            </Form.Item>
                        </Input.Group>
                    </Form.Item>
                </Col>
            </Row>
        ));

    const handleTimePeriodChange = (value: string) => {
        setIsDateRangePickerReq(value === "Custom");
    };

    const onAddGoalsSubmit = () => {
        form.submit();
        dispatch(setIsGoalFormSubmitted(true));
    };

    const onFinish = async (formValues: any) => {
        const {
            goalName,
            dataLevel,
            dataValueFilter,
            dataValueInput,
            metric,
            metricValueFilter,
            metricValueInput,
            timePeriod,
            priority,
            dateRange,
        } = formValues;

        const goalMetaData = getDataLevelConfig(goalsConfig, dataLevel);

        const goalsData: TGoalsData = {
            goalActionType: goalModalTitle.toLowerCase(),
            goalName,
            dataLevel,
            ...(isDataValueReq && {
                dataValueFilter: isDataFilterReq && dataValueFilter !== "Equals" ? dataValueFilter : "equals",
                dataValueInput: isArray(dataValueInput)
                    ? dataValueInput
                    : [dataValueInput],
            }),
            metric,
            metricUnit: goalMetaData.metric_level[metric],
            metricValueFilter: dataValueFilter !== "Equals" ? metricValueFilter : "equals",
            metricValueInput,
            timePeriod: {
                periodType: !isDateRangePickerReq ? "Standard" : "Custom",
                periodValue: !isDateRangePickerReq
                    ? timePeriod
                    : {
                        startDate: dayjs(dateRange[0]).format("YYYY-MM-DD"),
                        endDate: dayjs(dateRange[1]).format("YYYY-MM-DD"),
                    },
            },
            priority,
            conditions: map(conditions, (condition) => ({
                conditionLevel: condition.type || null,
                conditionValues: condition.value,
            })),
            ...(isGoalEdit && {
                goalId: goalEditConfig.goal_id,
                goalActiveStatus: goalEditConfig.goal_active_status,
            }),
        };
        await addGoals(goalsData);
        dispatch(updateAddGoalsModalDisplay(false));
    };

    const handleClose = () => {
        dispatch(updateAddGoalsModalDisplay(false));
        dispatch(setGoalEditConfig({} as TGoal));
    };

    // ----------goal edit logic----------
    useEffect(() => {
        if (isGoalEdit) {
            const goalDataLevel = goalEditConfig.data_level;
            const dataLevelConfig = getDataLevelConfig(
                goalsConfig,
                goalDataLevel
            );
            setDataValueFilter(goalEditConfig.data_level_filter);
            handleDataLevelChange(goalDataLevel as GoalDataLevels);
            setConditionCounter(goalEditConfig.goal_filter.length - 1);
            setConditions(
                map(goalEditConfig.goal_filter, (condition, i) => ({
                    key: i,
                    type: condition.conditionLevel,
                    value: condition.conditionValues,
                    options:
                        dataLevelConfig.filter_value[
                        condition.conditionLevel as string
                        ],
                })) as TCondition[]
            );
            setIsDateRangePickerReq(
                goalEditConfig.goal_time_period.periodType === "Custom"
            );

            //goal edit/clone initial form display values
            const initialValues = {
                goalName: goalEditConfig.goal_name,
                dataLevel: goalEditConfig.data_level,
                dataValueFilter: goalEditConfig.data_level_filter,
                dataValueInput: goalEditConfig.data_level_value,
                metric: goalEditConfig.metric_level,
                metricValueFilter: goalEditConfig.metric_level_condition,
                metricValueInput: goalEditConfig.metric_level_value,
                ...reduce(
                    goalEditConfig.goal_filter,
                    (acc, condition, i) => ({
                        ...acc,
                        [`condition${i}`]: condition.conditionLevel,
                        [`conditionValue${i}`]: condition.conditionValues,
                    }),
                    {}
                ),
                timePeriod:
                    goalEditConfig.goal_time_period.periodType === "Standard"
                        ? goalEditConfig.goal_time_period.periodValue
                        : "Custom",
                priority: goalEditConfig.goal_priority,
                ...(goalEditConfig.goal_time_period.periodType === "Custom" && {
                    dateRange: map(
                        values(goalEditConfig.goal_time_period.periodValue),
                        (date) => dayjs(date)
                    ),
                }),
            };

            form.setFieldsValue(initialValues);
        }
    }, [isGoalEdit, goalEditConfig, form]);

    return (
        <Modal
            title={`${goalModalTitle} Goal`}
            width={1000}
            open={show}
            onOk={handleClose}
            onCancel={handleClose}
            footer={[
                <Button key="back" onClick={handleClose}>
                    Cancel
                </Button>,
                <Button key="submit" type="primary" onClick={onAddGoalsSubmit}>
                    Submit
                </Button>,
            ]}
            style={{ top: 20 }}
        >
            <div className="add-goals-form-container">
                <Form
                    initialValues={{
                        dataValueFilter: "Equals",
                        metricValueFilter: "Equals"
                    }}
                    form={form}
                    layout="vertical"
                    onFinish={onFinish}
                    validateTrigger="onSubmit"
                >
                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                label="Goal Name"
                                name="goalName"
                                rules={[
                                    {
                                        required: true,
                                        message: "Please enter goal name",
                                    },
                                ]}
                                validateTrigger={
                                    isGoalFormSubmitted
                                        ? ["onChange", "onBlur"]
                                        : "onSubmit"
                                }
                            >
                                <Input
                                    placeholder="Enter goal name"
                                    style={{
                                        border: "1px solid #d9d9d9",
                                        borderRadius: "4px",
                                    }}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="Data Level"
                                name="dataLevel"
                                rules={[
                                    {
                                        required: true,
                                        message: "Please select a data level",
                                    },
                                ]}
                                validateTrigger={
                                    isGoalFormSubmitted
                                        ? ["onChange", "onBlur"]
                                        : "onSubmit"
                                }
                            >
                                <Select
                                    placeholder="Select data level"
                                    onChange={(value) =>
                                        handleDataLevelChange(value)
                                    }
                                    bordered={false}
                                    style={{
                                        border: "1px solid #d9d9d9",
                                        borderRadius: 4,
                                    }}
                                >
                                    {map(dataLevelOptions, (dataLevel) => (
                                        <Option value={dataLevel}>
                                            {dataLevel}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="Data Value"
                                required={isDataValueReq}
                                style={{
                                    marginBottom: 0,
                                    display: isDataValueReq ? "unset" : "none",
                                }}
                            >
                                <Input.Group
                                    compact
                                    style={{
                                        display: "flex",
                                        border: "1px solid #d9d9d9",
                                        borderRadius: 4,
                                        overflow: "hidden",
                                    }}
                                >
                                    <Form.Item
                                        name="dataValueFilter"
                                        style={{
                                            flex: 1,
                                            marginBottom: 0,
                                            display: isDataFilterReq
                                                ? "unset"
                                                : "none",
                                        }}
                                        rules={[
                                            {
                                                required: isDataFilterReq,
                                                message:
                                                    "Please select a filter",
                                            },
                                        ]}
                                        validateTrigger={
                                            isGoalFormSubmitted
                                                ? ["onChange", "onBlur"]
                                                : "onSubmit"
                                        }
                                    >
                                        <Select
                                            bordered={false}
                                            placeholder="Select filter"
                                            defaultValue="Equals"
                                            disabled={!isDataFilterReq}
                                            onChange={
                                                handleDataLevelFilterChange
                                            }
                                            style={{
                                                borderRight: isDataFilterReq
                                                    ? "1px solid #d9d9d9"
                                                    : "none",
                                                backgroundColor: "#EEEDEB",
                                                borderRadius: 0,
                                            }}
                                        >
                                            {dataValueFilterOptions.length &&
                                                dataValueFilterOptions.map(
                                                    (option: any) => (
                                                        <Option
                                                            key={option.value}
                                                            value={option.value}
                                                        >
                                                            {option.label}
                                                        </Option>
                                                    )
                                                )}
                                        </Select>
                                    </Form.Item>
                                    {renderDataValueInput()}
                                </Input.Group>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="Metric"
                                name="metric"
                                rules={[
                                    {
                                        required: true,
                                        message: "Please select a metric level",
                                    },
                                ]}
                                validateTrigger={
                                    isGoalFormSubmitted
                                        ? ["onChange", "onBlur"]
                                        : "onSubmit"
                                }
                            >
                                <Select
                                    placeholder="Select metric level"
                                    bordered={false}
                                    style={{
                                        borderRadius: 4,
                                        border: "1px solid #d9d9d9",
                                    }}
                                >
                                    {map(metricLevelOptions, (metricLevel) => (
                                        <Option value={metricLevel}>
                                            {metricLevel}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="Metric Value"
                                required
                                style={{ marginBottom: 0 }}
                            >
                                <Input.Group
                                    compact
                                    style={{
                                        display: "flex",
                                        border: "1px solid #d9d9d9",
                                        borderRadius: 4,
                                        overflow: "hidden",
                                    }}
                                >
                                    <Form.Item
                                        name="metricValueFilter"
                                        style={{ flex: 1, marginBottom: 0 }}
                                        rules={[
                                            {
                                                required: true,
                                                message:
                                                    "Please select a filter",
                                            },
                                        ]}
                                        validateTrigger={
                                            isGoalFormSubmitted
                                                ? ["onChange", "onBlur"]
                                                : "onSubmit"
                                        }
                                    >
                                        <Select
                                            bordered={false}
                                            defaultValue={"Equals"}
                                            placeholder="Select filter"
                                            style={{
                                                borderRight:
                                                    "1px solid #d9d9d9",
                                                backgroundColor: "#EEEDEB",
                                                borderRadius: 0,
                                            }}
                                        >
                                            {map(
                                                metricLevelFilterOptions,
                                                (filterOption) => (
                                                    <Option
                                                        key={filterOption.value}
                                                        value={
                                                            filterOption.value
                                                        }
                                                    >
                                                        {filterOption.label}
                                                    </Option>
                                                )
                                            )}
                                        </Select>
                                    </Form.Item>
                                    <Form.Item
                                        name="metricValueInput"
                                        style={{ flex: 2, marginBottom: 0 }}
                                        rules={[
                                            {
                                                required: true,
                                                message:
                                                    "Please enter a metric value",
                                            },
                                        ]}
                                        validateTrigger={
                                            isGoalFormSubmitted
                                                ? ["onChange", "onBlur"]
                                                : "onSubmit"
                                        }
                                    >
                                        <Input
                                            type="number"
                                            style={{
                                                border: "none",
                                                borderRadius: 0,
                                            }}
                                            placeholder="Enter metric value"
                                        />
                                    </Form.Item>
                                </Input.Group>
                            </Form.Item>
                        </Col>
                    </Row>
                    {renderExtraConditions()}
                    <Row gutter={16}>
                        <Form.Item>
                            <Button
                                className="mx-2"
                                type="primary"
                                onClick={handleAddCondition}
                            >
                                Add Condition
                            </Button>
                        </Form.Item>
                    </Row>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="Time Period"
                                name="timePeriod"
                                rules={[
                                    {
                                        required: true,
                                        message: "Please select a time period",
                                    },
                                ]}
                                validateTrigger={
                                    isGoalFormSubmitted
                                        ? ["onChange", "onBlur"]
                                        : "onSubmit"
                                }
                            >
                                <Select onChange={handleTimePeriodChange}>
                                    {map(goalPeriodOptions, (option) => (
                                        <Option value={option.value}>
                                            {option.value}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        {isDateRangePickerReq && (
                            <Col span={12}>
                                <Form.Item
                                    label="Date range"
                                    name="dateRange"
                                    rules={[
                                        {
                                            required: true,
                                            message:
                                                "Please select the custom range",
                                        },
                                    ]}
                                    validateTrigger={
                                        isGoalFormSubmitted
                                            ? ["onChange", "onBlur"]
                                            : "onSubmit"
                                    }
                                >
                                    <RangePicker
                                        style={{
                                            width: "100%",
                                        }}
                                    />
                                </Form.Item>
                            </Col>
                        )}
                    </Row>
                    <Row>
                        <Col span={12}>
                            <Form.Item
                                label="Priority"
                                name="priority"
                                rules={[
                                    {
                                        required: true,
                                        message:
                                            "Please select a priority level",
                                    },
                                ]}
                                validateTrigger={
                                    isGoalFormSubmitted
                                        ? ["onChange", "onBlur"]
                                        : "onSubmit"
                                }
                            >
                                <Radio.Group>
                                    <Radio value={"low"}>Low</Radio>
                                    <Radio value={"medium"}>Medium</Radio>
                                    <Radio value={"high"}>High</Radio>
                                </Radio.Group>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </div>
        </Modal>
    );
};

export default AddGoalsModal;
